import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DashboardIcon } from '@/components/icons/CustomIcons';
import { useBreadcrumbName } from '@/contexts/BreadcrumbNameContext';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  associados: 'Associados',
  empresas: 'Empresas',
  financeiro: 'Financeiro',
  administracao: 'Administração',
  cadastro: 'Cadastro',
  consulta: 'Consulta',
  'contas-receber': 'Contas a Receber',
  'contas-pagar': 'Contas a Pagar',
  inadimplencia: 'Inadimplência',
  'fluxo-caixa': 'Fluxo de Caixa',
  relatorios: 'Relatórios',
  'relatorios-mensais': 'Relatórios Mensais',
  'gerar-cobrancas-mensais': 'Gerar Cobranças',
  colonias: 'Colônias',
  hospedagem: 'Hospedagem',
  atividades: 'Atividades',
  comunicacao: 'Comunicação',
  mensagens: 'Mensagens',
  notificacoes: 'Notificações',
  eventos: 'Eventos',
  usuarios: 'Usuários',
  configuracoes: 'Configurações',
  controle: 'Controle',
  logs: 'Logs',
  clube: 'Clube',
  beneficios: 'Benefícios',
  editar: 'Editar',
};

// Aliases para caminhos que não possuem rota própria, redirecionando para o dashboard do módulo
const pathAliases: Record<string, string> = {
  '/colonias': '/colonias/dashboard',
  '/comunicacao': '/comunicacao/mensagens',
  '/administracao': '/administracao/usuarios',
  '/associados': '/associados',
  '/empresas': '/empresas',
  '/financeiro': '/financeiro',
};

// Segmentos que devem ser ignorados para manter o breadcrumb limpo e lógico
const skipSegments = ['clube', 'consulta'];

interface BreadcrumbsProps {
  lastLabelOverride?: string;
}

export function Breadcrumbs({ lastLabelOverride }: BreadcrumbsProps = {}) {
  const location = useLocation();
  const { breadcrumbName } = useBreadcrumbName();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const breadcrumbs: BreadcrumbItem[] = [];
  
  let currentPath = '';
  pathnames.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Pula segmentos puramente técnicos ou redundantes
    if (skipSegments.includes(segment)) {
      return;
    }

    const isLast = index === pathnames.length - 1;
    let label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    
    // Se for o último segmento e tivermos um nome dinâmico no contexto, usamos ele
    if (isLast && breadcrumbName) {
      label = breadcrumbName;
    } else if (isLast && lastLabelOverride) {
      label = lastLabelOverride;
    }

    // Aplica alias se o caminho intermediário não for uma rota válida por si só
    const path = pathAliases[currentPath] || currentPath;

    breadcrumbs.push({ label, path });
  });

  return (
    <nav className="flex items-center space-x-1 text-sm text-muted-foreground">
      <Link
        to="/"
        className="flex items-center hover:text-foreground transition-colors"
        title="Início"
      >
        <DashboardIcon className="h-4 w-4" size={16} />
      </Link>
      
      {breadcrumbs.map((breadcrumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        
        return (
          <React.Fragment key={`${breadcrumb.path}-${index}`}>
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
            {isLast ? (
              <span className="text-foreground font-medium truncate max-w-[200px]">
                {breadcrumb.label}
              </span>
            ) : (
              <Link
                to={breadcrumb.path}
                className="hover:text-foreground transition-colors whitespace-nowrap"
              >
                {breadcrumb.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
